import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper
} from '@mui/material';
import {
  Visibility as ViewIcon,
  CheckCircle as ApplyIcon,
  Cancel as DismissIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from '../../../api/axiosConfig';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  CompaniesHouseAudit,
  CompaniesHouseAuditStats,
  CompaniesHouseAuditFilters
} from '../../../types/companiesHouseAudit.types';

const DataQualityTab: React.FC = () => {
  const [audits, setAudits] = useState<CompaniesHouseAudit[]>([]);
  const [stats, setStats] = useState<CompaniesHouseAuditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedAudit, setSelectedAudit] = useState<CompaniesHouseAudit | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reviewNotesOpen, setReviewNotesOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionType, setActionType] = useState<'apply' | 'dismiss' | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedAuditIds, setSelectedAuditIds] = useState<string[]>([]);
  const [bulkApplying, setBulkApplying] = useState(false);

  // Filters and pagination
  const [filters, setFilters] = useState<CompaniesHouseAuditFilters>({
    status: 'pending',
    page: 1,
    limit: 25,
    sortBy: 'auditDate',
    sortOrder: 'desc'
  });
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    fetchAudits();
    fetchStats();
  }, [filters]);

  const fetchAudits = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.noLongerTrading) params.append('noLongerTrading', 'true');
      if (filters.significantChange) params.append('significantChange', 'true');
      params.append('page', filters.page?.toString() || '1');
      params.append('limit', filters.limit?.toString() || '25');
      params.append('sortBy', filters.sortBy || 'auditDate');
      params.append('sortOrder', filters.sortOrder || 'desc');

      const response = await axios.get(`/companies-house-audit?${params.toString()}`);
      setAudits(response.data.data);
      setTotalRecords(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching audits:', error);
      toast.error('Failed to load audit records');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/companies-house-audit/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleViewDetails = (audit: CompaniesHouseAudit) => {
    setSelectedAudit(audit);
    setDetailsOpen(true);
  };

  const handleApply = (audit: CompaniesHouseAudit) => {
    setSelectedAudit(audit);
    setActionType('apply');
    setReviewNotesOpen(true);
  };

  const handleDismiss = (audit: CompaniesHouseAudit) => {
    setSelectedAudit(audit);
    setActionType('dismiss');
    setReviewNotesOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedAudit || !actionType) return;

    try {
      const endpoint = actionType === 'apply' 
        ? `/companies-house-audit/${selectedAudit._id}/apply`
        : `/companies-house-audit/${selectedAudit._id}/dismiss`;

      await axios.post(endpoint, { reviewNotes });

      toast.success(`Changes ${actionType === 'apply' ? 'applied' : 'dismissed'} successfully`);
      setReviewNotesOpen(false);
      setReviewNotes('');
      setActionType(null);
      setSelectedAudit(null);
      fetchAudits();
      fetchStats();
    } catch (error) {
      console.error('Error performing action:', error);
      toast.error(`Failed to ${actionType} changes`);
    }
  };

  const handleBulkApply = async () => {
    if (selectedAuditIds.length === 0) {
      toast.error('Please select at least one audit record');
      return;
    }

    try {
      setBulkApplying(true);
      const response = await axios.post('/companies-house-audit/bulk-apply', {
        auditIds: selectedAuditIds
      });

      const { applied, failed } = response.data.results;
      toast.success(`Successfully applied ${applied} changes. ${failed > 0 ? `${failed} failed.` : ''}`);
      
      setSelectedAuditIds([]);
      fetchAudits();
      fetchStats();
    } catch (error) {
      console.error('Error applying bulk changes:', error);
      toast.error('Failed to apply bulk changes');
    } finally {
      setBulkApplying(false);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const pendingAuditIds = audits
        .filter(audit => audit.status === 'pending')
        .map(audit => audit._id);
      setSelectedAuditIds(pendingAuditIds);
    } else {
      setSelectedAuditIds([]);
    }
  };

  const handleSelectAudit = (auditId: string) => {
    setSelectedAuditIds(prev => {
      if (prev.includes(auditId)) {
        return prev.filter(id => id !== auditId);
      } else {
        return [...prev, auditId];
      }
    });
  };

  const handleTriggerRefresh = async () => {
    try {
      setRefreshing(true);
      await axios.post('/companies-house-audit/trigger-refresh');
      toast.success('Companies House refresh started. This may take several minutes.');
    } catch (error) {
      console.error('Error triggering refresh:', error);
      toast.error('Failed to trigger refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'reviewed': return 'info';
      case 'applied': return 'success';
      case 'dismissed': return 'default';
      default: return 'default';
    }
  };

  const formatFieldName = (field: string) => {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/\./g, ' > ')
      .replace(/^./, str => str.toUpperCase());
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Companies House Data Quality</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {selectedAuditIds.length > 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={bulkApplying ? <CircularProgress size={20} /> : <ApplyIcon />}
              onClick={handleBulkApply}
              disabled={bulkApplying}
            >
              {bulkApplying ? 'Applying...' : `Apply ${selectedAuditIds.length} Selected`}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleTriggerRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Trigger Refresh'}
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Pending</Typography>
                <Typography variant="h4">{stats.totalPending}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Reviewed</Typography>
                <Typography variant="h4">{stats.totalReviewed}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Applied</Typography>
                <Typography variant="h4" color="success.main">{stats.totalApplied}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Dismissed</Typography>
                <Typography variant="h4">{stats.totalDismissed}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'error.light' }}>
              <CardContent>
                <Typography color="white" gutterBottom>Not Trading</Typography>
                <Typography variant="h4" color="white">{stats.noLongerTrading}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'warning.light' }}>
              <CardContent>
                <Typography color="white" gutterBottom>Significant</Typography>
                <Typography variant="h4" color="white">{stats.significantChanges}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value as any, page: 1 })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="reviewed">Reviewed</MenuItem>
                  <MenuItem value="applied">Applied</MenuItem>
                  <MenuItem value="dismissed">Dismissed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.noLongerTrading || false}
                    onChange={(e) => setFilters({ ...filters, noLongerTrading: e.target.checked, page: 1 })}
                  />
                }
                label="No Longer Trading"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.significantChange || false}
                    onChange={(e) => setFilters({ ...filters, significantChange: e.target.checked, page: 1 })}
                  />
                }
                label="Significant Changes"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Audit Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {filters.status === 'pending' && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedAuditIds.length > 0 && selectedAuditIds.length < audits.filter(a => a.status === 'pending').length}
                      checked={audits.filter(a => a.status === 'pending').length > 0 && selectedAuditIds.length === audits.filter(a => a.status === 'pending').length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                <TableCell>Company</TableCell>
                <TableCell>Company Number</TableCell>
                <TableCell>Audit Date</TableCell>
                <TableCell>Changes</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={filters.status === 'pending' ? 8 : 7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : audits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={filters.status === 'pending' ? 8 : 7} align="center">
                    <Typography color="textSecondary">No audit records found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                audits.map((audit) => (
                  <TableRow key={audit._id} hover selected={selectedAuditIds.includes(audit._id)}>
                    {filters.status === 'pending' && audit.status === 'pending' && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedAuditIds.includes(audit._id)}
                          onChange={() => handleSelectAudit(audit._id)}
                        />
                      </TableCell>
                    )}
                    {filters.status === 'pending' && audit.status !== 'pending' && (
                      <TableCell padding="checkbox" />
                    )}
                    <TableCell>{audit.client.companyName}</TableCell>
                    <TableCell>{audit.companyNumber}</TableCell>
                    <TableCell>{format(new Date(audit.auditDate), 'dd/MM/yyyy HH:mm')}</TableCell>
                    <TableCell>
                      <Chip label={`${audit.changes.length} changes`} size="small" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {audit.flags.noLongerTrading && (
                          <Tooltip title="Company no longer trading">
                            <Chip icon={<WarningIcon />} label="Not Trading" size="small" color="error" />
                          </Tooltip>
                        )}
                        {audit.flags.significantChange && (
                          <Tooltip title="Significant change detected">
                            <Chip icon={<InfoIcon />} label="Significant" size="small" color="warning" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={audit.status} size="small" color={getStatusColor(audit.status)} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleViewDetails(audit)}>
                        <ViewIcon />
                      </IconButton>
                      {audit.status === 'pending' && (
                        <>
                          <IconButton size="small" color="success" onClick={() => handleApply(audit)}>
                            <ApplyIcon />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDismiss(audit)}>
                            <DismissIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalRecords}
          page={(filters.page || 1) - 1}
          onPageChange={(_, newPage) => setFilters({ ...filters, page: newPage + 1 })}
          rowsPerPage={filters.limit || 25}
          onRowsPerPageChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value), page: 1 })}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      {/* Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(25, 25, 25, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          Audit Details: {selectedAudit?.client.companyName}
        </DialogTitle>
        <DialogContent>
          {selectedAudit && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>Changes Detected</Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Current Value</TableCell>
                      <TableCell>New Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedAudit.changes.map((change, index) => (
                      <TableRow key={index}>
                        <TableCell><strong>{formatFieldName(change.field)}</strong></TableCell>
                        <TableCell>{change.currentValue || <em>empty</em>}</TableCell>
                        <TableCell sx={{ bgcolor: 'success.light' }}>{change.newValue || <em>empty</em>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {selectedAudit.flags.noLongerTrading && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <strong>Warning:</strong> This company is no longer trading. 
                  Status: {selectedAudit.companiesHouseData.company_status}
                </Alert>
              )}

              {selectedAudit.reviewNotes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>Review Notes</Typography>
                  <Typography>{selectedAudit.reviewNotes}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Review Notes Dialog */}
      <Dialog 
        open={reviewNotesOpen} 
        onClose={() => setReviewNotesOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(25, 25, 25, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          {actionType === 'apply' ? 'Apply Changes' : 'Dismiss Changes'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notes (optional)"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewNotesOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmAction} 
            variant="contained"
            color={actionType === 'apply' ? 'success' : 'error'}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataQualityTab;
